import { useState } from 'react';
import { Link } from 'react-router';
import { Avatar } from '@/components/brand/Brand';
import { Button } from '@/components/ui/Button';
import { Sheet } from '@/components/ui/Sheet';
import { Skeleton } from '@/components/ui/Feedback';
import { useFriendRequests, useFriends, useHandicap, usePlayingPartners } from '@/lib/api/queries';
import { useCurrentUser } from '@/lib/auth/AuthContext';
import { fmtDecimal } from '@/lib/format/number';
import { FriendSearch } from '@/routes/friends/FriendSearch';
import { useRespondToRequest } from '@/routes/friends/useRespondToRequest';
import { routes } from '@/routes/paths';
import styles from './TimelineSidebar.module.css';
import { t } from '@/lib/i18n';

/**
 * A lateral da timeline no desktop (canvas 1u).
 *
 * Ela recebe o que no celular exige navegação — identidade, pedidos pendentes e a lista de
 * amigos —, de modo que 1p e 1q convivam sem troca de contexto. Não é uma segunda coluna de
 * feed: o espaço extra vai para dado, não para mais cartões.
 *
 * Só existe a partir de 900px, e por isso o `aria-hidden` do celular não faz falta: o CSS a
 * remove do fluxo com `display: none`, o que também a tira da ordem de leitura.
 */
export function TimelineSidebar() {
  const user = useCurrentUser();
  const handicap = useHandicap();
  const friends = useFriends();
  const requests = useFriendRequests();
  const partners = usePlayingPartners();
  const { working, respond } = useRespondToRequest();
  const [searchOpen, setSearchOpen] = useState(false);

  const list = friends.data ?? [];
  const received = requests.data?.received ?? [];

  /*
   * "Voltas juntos" é a soma das voltas com cada amigo, não o total de voltas em grupo: o
   * canvas mostra 32 ao lado de quatro amigos que somam 14 + 6 + 3 + 9. Uma volta com três
   * amigos conta três vezes porque a pergunta é "quanto eu jogo com essa turma", não
   * "quantas voltas eu joguei".
   */
  const roundsTogether = list.reduce((sum, f) => sum + (partners.data?.get(f.id) ?? 0), 0);

  return (
    <aside className={styles.sidebar} aria-label={t('sidebar.aria')}>
      <section className={styles.identity}>
        <Link to={routes.profile} className={styles.identityHead}>
          <Avatar name={user.displayName} size={48} variant="green" />
          <span className={styles.identityBody}>
            <span className={styles.identityName}>{user.displayName}</span>
            <span className={styles.identityMeta}>
              {describeIndex(handicap.data?.handicapIndex ?? null)} ·{' '}
              {t('rounds.count', { count: handicap.data?.basedOnRounds ?? 0 })}
            </span>
          </span>
        </Link>

        <div className={styles.identityStats}>
          <div>
            <div className={styles.statValue}>{list.length}</div>
            <div className={styles.statLabel}>{t('sidebar.friends')}</div>
          </div>
          <div>
            <div className={styles.statValue}>{roundsTogether}</div>
            <div className={styles.statLabel}>{t('sidebar.roundsTogether')}</div>
          </div>
        </div>
      </section>

      {received.length > 0 && (
        <section className={styles.block}>
          <h2 className={styles.blockTitle}>
            Pedidos
            <span className={styles.blockCount}>{received.length}</span>
          </h2>
          {received.map((request) => (
            <div key={request.id} className={styles.request}>
              <div className={styles.person}>
                <Avatar name={request.user.displayName} size={34} variant="green" />
                <div className={styles.personBody}>
                  <div className={styles.personName}>{request.user.displayName}</div>
                  <div className={styles.personMeta}>{describeRequester(request.user)}</div>
                </div>
              </div>
              <div className={styles.requestActions}>
                <Button
                  size="sm"
                  busy={working === request.id}
                  onClick={() => void respond(request, true)}
                >
                  Aceitar
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={working === request.id}
                  onClick={() => void respond(request, false)}
                >
                  Recusar
                </Button>
              </div>
            </div>
          ))}
        </section>
      )}

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>
          {/*
            O título leva à página de Amigos: com o hero escondido no desktop, esta é a única
            porta para ela — e é onde se recusa um pedido antigo ou se desfaz uma amizade.
          */}
          <Link to={routes.friends} className={styles.blockTitleLink}>
            {t('sidebar.friends')}
          </Link>
          <button type="button" className={styles.addLink} onClick={() => setSearchOpen(true)}>
            {t('sidebar.addFriend')}
          </button>
        </h2>

        {friends.isLoading ? (
          <FriendRowsSkeleton />
        ) : list.length === 0 ? (
          <p className={styles.emptyNote}>
            {t('timeline.empty.body')}
          </p>
        ) : (
          list.map((friend) => (
            <div key={friend.id} className={styles.friendRow}>
              <div className={styles.person}>
                <Avatar name={friend.displayName} size={34} variant="green" />
                <div className={styles.personBody}>
                  <div className={styles.personName}>{friend.displayName}</div>
                  <div className={styles.personMeta}>
                    {describeTogether(partners.data?.get(friend.id) ?? 0)}
                  </div>
                </div>
              </div>
              {/* O handicap é o único número da lista: é como o golfista se situa (canvas 1q). */}
              <div className={styles.friendHcp}>{fmtDecimal(friend.handicapIndex)}</div>
            </div>
          ))
        )}
      </section>

      <Sheet open={searchOpen} onClose={() => setSearchOpen(false)} title="Adicionar amigo">
        <FriendSearch autoFocus />
      </Sheet>
    </aside>
  );
}

function describeIndex(handicapIndex: number | null): string {
  return handicapIndex === null
    ? t('sidebar.noHandicap')
    : t('friends.handicap', { value: fmtDecimal(handicapIndex) ?? '' });
}

/** "hcp 16,8 · 2 em comum" — a linha curta do pedido, que a lateral estreita comporta. */
function describeRequester(user: { handicapIndex: number | null }): string {
  return user.handicapIndex === null
    ? t('sidebar.noHandicap')
    : t('sidebar.handicapShort', { value: fmtDecimal(user.handicapIndex) ?? '' });
}

/**
 * "14 voltas juntos". Zero não vira "0 voltas juntos": para quem acabou de virar amigo isso
 * leria como um placar, e o que a linha quer dizer é que ainda não houve nenhuma.
 */
function describeTogether(rounds: number): string {
  if (rounds === 0) return t('friends.neverPlayedTogether');
  return t('friends.roundsTogether', { count: rounds });
}

function FriendRowsSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div key={i} className={styles.friendRow}>
          <div className={styles.person}>
            <Skeleton width={34} height={34} radius="999px" />
            <div className={styles.personBody}>
              <Skeleton width="60%" height={12} />
              <Skeleton width="40%" height={9} style={{ marginTop: 5 }} />
            </div>
          </div>
        </div>
      ))}
    </>
  );
}
